import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Product lines — brands and models a merchant sells ("UGG", "Nike P-6000").
 *
 * A line is a rule, not a list: every connected supplier's products that match it are
 * selected, tagged with the line's tag, and (on Shopify) gathered in a smart collection on
 * that tag. New supplier products that match are picked up by the hourly refresh.
 *
 * Everything goes through the merchant-product-lines edge function: the tables are
 * read-only for clients, and saving also re-matches and creates the collection.
 *
 * `catalog: true` is onboarding's "what do you want to sell?", asked BEFORE any supplier is
 * connected: brands, previews and counts then come from every active supplier, so the
 * merchant sees what exists and who carries it. Saved lines are the same either way; they
 * select products only once a supplier carrying them is connected.
 */

export type ApprovalEmailSetting = { enabled: boolean; allowed: boolean; email: string | null };

export type SelectionMode = 'select_all' | 'deselect_all';

/**
 * What a line does with a NEW matching supplier product: 'auto' imports it at the next sync
 * (the default), 'approval' puts it in the approval queue until the merchant decides.
 */
export type ApprovalMode = 'auto' | 'approval';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ProductLine {
  id: string;
  name: string;
  tag: string;
  brand: string | null;
  patterns: string[];
  exclude_patterns: string[];
  footwear_only: boolean;
  provider_ids: string[] | null;
  approval_mode: ApprovalMode;
  pending_count: number;
  /** Raw matches by bucket at the last re-match (src/lib/lineCounts.ts); null before one. */
  match_breakdown: unknown;
  collection_id: string | null;
  collection_error: string | null;
  matched_count: number;
  last_matched_at: string | null;
}

export interface LineDraft {
  id?: string;
  name: string;
  tag: string;
  brand: string | null;
  patterns: string[];
  exclude_patterns: string[];
  footwear_only: boolean;
  provider_ids: string[] | null;
  // Omitted = keep the line's current mode (new lines: 'auto').
  approval_mode?: ApprovalMode;
  create_collection?: boolean;
}

export interface LineProvider {
  id: string;
  name: string;
  selection_mode: SelectionMode;
}

export interface Brand {
  brand: string;
  label: string;
  products: number;
}

export interface LinePreview {
  products: number;
  per_provider: Array<{ provider_id: string; name: string; products: number }>;
  sample: Array<{ sku: string; title: string; photo_url: string | null }>;
  /** Same buckets as ProductLine.match_breakdown; absent on the pre-connection catalog preview. */
  breakdown?: unknown;
}

export interface ModePreview {
  mode: SelectionMode;
  providers_changing: number;
  products_selected_now: number;
  products_selected_after: number;
  products_removed: number;
  units_removed: number;
  products_added: number;
}

/** Onboarding: who carries the shop's saved lines, across every active supplier. */
export interface CatalogCounts {
  products: number;
  lines: Record<string, { products: number; suppliers: number }>;
  suppliers: Array<{ provider_id: string; products: number; lines: string[] }>;
}

export interface ApprovalItem {
  sku: string;
  status: ApprovalStatus;
  title: string | null;
  vendor: string | null;
  photo_url: string | null;
  stock: number;
  price: number | null;
  suppliers: Array<{ id: string; name: string; stock: number; price: number | null }>;
  lines: Array<{ id: string; name: string }>;
  first_seen_at: string;
  decided_at: string | null;
  decided_via: string | null;
}

export interface ApprovalList {
  counts: Record<ApprovalStatus, number>;
  items: ApprovalItem[];
}

export interface SaveResult {
  line_id: string;
  matched: number;
  collection_id?: string;
  collection_created?: boolean;
  collection_error?: string;
  refresh_error?: string;
}

export const useProductLines = (shopId: string | null, opts: { catalog?: boolean } = {}) => {
  const catalog = opts.catalog === true;
  const [lines, setLines] = useState<ProductLine[]>([]);
  const [counts, setCounts] = useState<CatalogCounts | null>(null);
  const [providers, setProviders] = useState<LineProvider[]>([]);
  const [approvals, setApprovals] = useState<{ pending: number; rejected: number }>({ pending: 0, rejected: 0 });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(
    async <T,>(action: string, payload: Record<string, unknown> = {}): Promise<T> => {
      if (!shopId) throw new Error('No shop selected');
      const { data, error: fnErr } = await supabase.functions.invoke('merchant-product-lines', {
        body: { shop_id: shopId, action, ...payload },
      });
      if (fnErr) {
        // The function's own message is in the response body; prefer it.
        const body = await (fnErr as any)?.context?.json?.().catch(() => null);
        throw new Error(body?.error || fnErr.message);
      }
      if (data?.error) throw new Error(data.error);
      return data as T;
    },
    [shopId],
  );

  const reload = useCallback(async () => {
    if (!shopId) {
      setLines([]);
      setProviders([]);
      setLoaded(false);
      return;
    }
    try {
      setError(null);
      const data = await call<{
        lines: ProductLine[];
        providers: LineProvider[];
        approvals?: { pending: number; rejected: number };
      }>('list');
      setLines(data.lines ?? []);
      setProviders(data.providers ?? []);
      setApprovals(data.approvals ?? { pending: 0, rejected: 0 });
      if (catalog) setCounts(await call<CatalogCounts>('catalog_suppliers'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load product lines');
    } finally {
      setLoaded(true);
    }
  }, [shopId, call, catalog]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadBrands = useCallback(async () => {
    const data = await call<{ brands: Brand[] }>(catalog ? 'catalog_brands' : 'brands');
    setBrands(data.brands ?? []);
  }, [call, catalog]);

  const preview = useCallback(
    (line: LineDraft) => call<LinePreview>(catalog ? 'catalog_preview' : 'preview', { line }),
    [call, catalog],
  );

  const save = useCallback(
    async (line: LineDraft) => {
      const result = await call<SaveResult>('save', { line });
      await reload();
      return result;
    },
    [call, reload],
  );

  const remove = useCallback(
    async (lineId: string) => {
      await call('delete', { line_id: lineId });
      await reload();
    },
    [call, reload],
  );

  const createCollection = useCallback(
    async (lineId: string) => {
      const result = await call<{ collection_id?: string; collection_error?: string }>(
        'collection',
        { line_id: lineId },
      );
      await reload();
      return result;
    },
    [call, reload],
  );

  const previewMode = useCallback(
    (mode: SelectionMode) => call<ModePreview>('mode_preview', { mode }),
    [call],
  );

  const setMode = useCallback(
    // includeFuture: suppliers connected later get this mode too (onboarding).
    async (mode: SelectionMode, includeFuture = false) => {
      const result = await call<{ providers_changed: number }>('set_mode', {
        mode,
        ...(includeFuture ? { include_future: true } : {}),
      });
      await reload();
      return result;
    },
    [call, reload],
  );

  const refresh = useCallback(() => call('refresh'), [call]);

  const listApprovals = useCallback(
    (status: ApprovalStatus, offset = 0, limit = 100) =>
      call<ApprovalList>('approvals', { status, offset, limit }),
    [call],
  );

  const decide = useCallback(
    // The line counts change with every decision, so the list is reloaded too.
    async (skus: string[], decision: 'approved' | 'rejected') => {
      const result = await call<{ changed: number; refresh_error?: string }>('decide', { skus, decision });
      await reload();
      return result;
    },
    [call, reload],
  );

  // Per member (Y2): the signed-in user's own opt-in, mailed to their login email.
  // `allowed` is false for roles that cannot get it (only owner/admin can).
  const approvalEmail = useCallback(
    async (enabled?: boolean): Promise<ApprovalEmailSetting> => {
      const r = await call<Partial<ApprovalEmailSetting>>('approval_email', enabled === undefined ? {} : { enabled });
      return { enabled: r.enabled === true, allowed: r.allowed !== false, email: r.email ?? null };
    },
    [call],
  );

  return {
    lines,
    counts,
    providers,
    approvals,
    brands,
    loaded,
    error,
    reload,
    loadBrands,
    preview,
    save,
    remove,
    createCollection,
    previewMode,
    setMode,
    refresh,
    listApprovals,
    decide,
    approvalEmail,
  };
};
